import { HubConnectionBuilder, HttpTransportType, LogLevel, type HubConnection } from "@microsoft/signalr";
import { useApiClient } from "@positive/api-client";
import { useFormatter, type ShellNotification } from "@positive/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useIdentity } from "../identity/IdentityProvider";
import {
  notificationHubPath,
  notificationKeys,
  notificationsApi,
  type NotificationEntry,
  type NotificationPage,
} from "./notificationsApi";

/** Arguments ending in `At` are instants; they are shown in the user's format. */
const isInstant = (name: string) => name.endsWith("At");

export interface NotificationCenterState {
  notifications: readonly ShellNotification[];
  unread: number;
  markAllRead: () => void;
  open: (notification: ShellNotification) => string | null;
}

/**
 * Feeds the Shell Bar notification centre (D14-9): loads the user's notifications and keeps them live over SignalR
 * (`/api/msg/hub`, one group per tenant user; reconnects automatically). Tenant hosts only.
 */
export function useNotificationCenter(): NotificationCenterState {
  const { t } = useTranslation("platform");
  const formatter = useFormatter();
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { auth, mode, session, supportSessionId } = useIdentity();
  const enabled = mode.kind === "tenant" && session !== null;

  const query = useQuery({
    queryKey: notificationKeys.list,
    queryFn: ({ signal }) => notificationsApi.list(client, signal),
    enabled,
    // Without a live connection (hub unavailable) the list still refreshes now and then.
    refetchInterval: 5 * 60_000,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!enabled) return;
    // Browsers cannot send custom headers on WebSockets: with the development tenant header or a support session
    // header the connection falls back to long polling, which carries them.
    const headers: Record<string, string> = {};
    if (mode.devTenantHeader) headers["X-Positive-Tenant"] = mode.slug;
    if (supportSessionId) headers["X-Positive-Support-Session"] = supportSessionId;
    const needsHeaders = Object.keys(headers).length > 0;
    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(notificationHubPath, {
        accessTokenFactory: async () => (await auth.getAccessToken()) ?? "",
        ...(needsHeaders ? { headers, transport: HttpTransportType.LongPolling } : {}),
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.None)
      .build();
    connection.on("notification", (entry: NotificationEntry, unreadCount: number) => {
      queryClient.setQueryData<NotificationPage>(notificationKeys.list, (current) =>
        current
          ? {
              items: [entry, ...current.items.filter((item) => item.id !== entry.id)],
              totalCount: current.totalCount + 1,
              unreadCount,
            }
          : current,
      );
    });
    connection.on("unreadCount", (unreadCount: number) => {
      queryClient.setQueryData<NotificationPage>(notificationKeys.list, (current) =>
        current ? { ...current, unreadCount } : current,
      );
      void queryClient.invalidateQueries({ queryKey: notificationKeys.list });
    });
    // The hub is optional: without it the list refreshes on its interval.
    void connection.start().catch(() => undefined);
    return () => {
      void connection.stop();
    };
  }, [auth, enabled, mode, queryClient, supportSessionId]);

  const notifications = useMemo<readonly ShellNotification[]>(
    () =>
      (query.data?.items ?? []).map((entry) => {
        const args: Record<string, string> = {};
        for (const [name, value] of Object.entries(entry.args)) {
          args[name] = isInstant(name) ? formatter.dateTime(value) : value;
        }
        if (entry.args["kind"]) args["kind"] = t(`notifications.exportKinds.${entry.args["kind"]}`, entry.args["kind"]);
        if (entry.args["docType"])
          args["docType"] = t(`workflow.docTypes.${entry.args["docType"]}`, entry.args["docType"]);
        if (entry.args["status"])
          args["status"] = t(`notifications.statuses.${entry.args["status"]}`, entry.args["status"]);
        return {
          id: entry.id,
          title: t(entry.titleKey, args),
          ...(entry.bodyKey ? { body: t(entry.bodyKey, args) } : {}),
          severity: entry.severity,
          createdAt: formatter.dateTime(entry.createdAt),
          read: entry.readAt !== null,
          ...(entry.link ? { link: entry.link } : {}),
        };
      }),
    [formatter, query.data, t],
  );

  return {
    notifications,
    unread: query.data?.unreadCount ?? 0,
    markAllRead: () => {
      void notificationsApi
        .markAllRead(client)
        .then(() => queryClient.invalidateQueries({ queryKey: notificationKeys.list }));
    },
    open: (notification) => {
      if (!notification.read) {
        void notificationsApi
          .markRead(client, notification.id)
          .then(() => queryClient.invalidateQueries({ queryKey: notificationKeys.list }));
      }
      return notification.link ?? null;
    },
  };
}
